import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import {
  Country,
  Province,
  District,
  SubDistrict,
  ExcludedCountry,
  ImdCodeName,
  Discount,
  InsuranceType,
  InsurancePlan,
  Title,
  Relationship,
  Nationality,
  CustomerSegment,
  AoMember,
  MarketingUser,
  Channel,
  BusinessUnit,
  BusinessType,
  Occupation,
  Consent
} from '@model/master-data.model';
import { PartyType } from '@model/party.model';
import { environment } from '@environments/environment';
import { CacheService } from './cache.service';

@Injectable({
  providedIn: 'root'
})
export class MasterDataService {
  constructor(private http: HttpClient, private cacheService: CacheService) {}

  getCountry(): Observable<Country[]> {
    const url: string = `${environment.endpoint}/countries`;
    const request: Observable<Country[]> = this.http.get<Country[]>(url);

    return this.cacheService.cache(url, request);
  }

  getCountryBySubProductId(subProductId: string): Observable<Country[]> {
    const url: string = `${environment.endpoint}/subProduct/${subProductId}/countries`;
    return this.http.get<Country[]>(url);
  }

  getProvince(): Observable<Province[]> {
    const url: string = `${environment.endpoint}/provinces`;
    const request: Observable<Province[]> = this.http.get<Province[]>(url);

    return this.cacheService.cache(url, request);
  }

  getProvinceBySubProductId(subProductId: string): Observable<Province[]> {
    const url: string = `${environment.endpoint}/subProduct/${subProductId}/provinces`;
    const request: Observable<Province[]> = this.http.get<Province[]>(url);

    return this.cacheService.cache(url, request);
  }

  getExcludedCountries(): Observable<ExcludedCountry[]> {
    const url: string = `${environment.endpoint}/excludedCountries`;
    const request: Observable<ExcludedCountry[]> = this.http.get<Province[]>(url);

    return this.cacheService.cache(url, request);
  }

  getDistrict(provinceCode: string): Observable<District[]> {
    const url: string = `${environment.endpoint}/province/${provinceCode}/districts`;
    const request: Observable<District[]> = this.http.get<District[]>(url);

    return this.cacheService.cache(url, request);
  }

  getSubDistrict(provinceCode: string, districtCode: string): Observable<Province[]> {
    const url: string = `${environment.endpoint}/province/${provinceCode}/district/${districtCode}/subdistricts`;
    const request: Observable<Province[]> = this.http.get<SubDistrict[]>(url);

    return this.cacheService.cache(url, request);
  }

  getImdCode(branchCode: string): Observable<ImdCodeName[]> {
    const url: string = `${environment.endpoint}/branches/${branchCode}/intermediaries`;
    const request: Observable<ImdCodeName[]> = this.http.get<ImdCodeName[]>(url);

    return this.cacheService.cache(url, request);
  }

  getAoMember(intermediaryId: string): Observable<AoMember[]> {
    const url: string = `${environment.endpoint}/intermediaries/${intermediaryId}/aomembers`;
    return this.http.get<AoMember[]>(url);
  }

  getDiscount(subProductId: string): Observable<Discount[]> {
    const url: string = `${environment.endpoint}/subproducts/${subProductId}/directdiscountgroups`;
    return this.http.get<Discount[]>(url);
  }

  getInsuranceType(packageType: string): Observable<InsuranceType[]> {
    const url: string = `${environment.endpoint}/package/${packageType}/insuranceType`;
    const request: Observable<InsuranceType[]> = this.http.get<InsuranceType[]>(url);

    return this.cacheService.cache(url, request);
  }

  getInsurancePlan(categoryId: string): Observable<InsurancePlan[]> {
    const url: string = `${environment.endpoint}/category/${categoryId}/insurancePlan`;
    const request: Observable<InsurancePlan[]> = this.http.get<InsurancePlan[]>(url);

    return this.cacheService.cache(url, request);
  }

  getNationality(): Observable<Nationality[]> {
    const url: string = `${environment.endpoint}/nationalities`;
    const request: Observable<Nationality[]> = this.http.get<Nationality[]>(url);

    return this.cacheService.cache(url, request);
  }

  getTitle(): Observable<Title[]> {
    const url: string = `${environment.endpoint}/titles`;
    const request: Observable<Title[]> = this.http.get<Title[]>(url);

    return this.cacheService.cache(url, request);
  }

  getOccupation(): Observable<Occupation[]> {
    const url: string = `${environment.endpoint}/occupations`;
    const request: Observable<Occupation[]> = this.http.get<Occupation[]>(url);

    return this.cacheService.cache(url, request);
  }

  getCustomerSegment(partyType: PartyType): Observable<CustomerSegment[]> {
    if (!partyType) {
      return of([]);
    }

    const url: string = `${environment.endpoint}/customersegments?partyType=${partyType}`;
    const request: Observable<CustomerSegment[]> = this.http.get<CustomerSegment[]>(url);

    return this.cacheService.cache(url, request);
  }

  getRelationship(): Observable<Relationship[]> {
    const url: string = `${environment.endpoint}/relationships`;
    const request: Observable<Relationship[]> = this.http.get<Relationship[]>(url);

    return this.cacheService.cache(url, request);
  }

  getChannel(): Observable<Channel[]> {
    const url: string = `${environment.endpoint}/channels`;
    const request: Observable<Channel[]> = this.http.get<Channel[]>(url);

    return this.cacheService.cache(url, request);
  }

  getMarketingUser(): Observable<MarketingUser[]> {
    const url: string = `${environment.endpoint}/marketingusers`;
    return this.http.get<MarketingUser[]>(url);
  }

  getBusinessUnit(customerSegment: string, partyId: string, partyType: string): Observable<BusinessUnit[]> {
    const url: string = `${
      environment.endpoint
    }/businessunits?customerSegment=${customerSegment}&partyId=${partyId}&partyType=${partyType}`;

    const request: Observable<BusinessUnit[]> = this.http.get<BusinessUnit[]>(url).pipe(distinctUntilChanged());

    return customerSegment ? this.cacheService.cache(url, request) : of(null);
  }

  getBusinessType(): Observable<BusinessType[]> {
    const url: string = `${environment.endpoint}/businesstypes`;
    return this.http.get<BusinessType[]>(url);
  }

  getConsent(): Observable<Consent[]> {
    const url: string = `${environment.endpoint}/consents`;
    const request: Observable<Consent[]> = this.http.get<Consent[]>(url);

    return this.cacheService.cache(url, request);
  }
}
